import { faDiagramNested } from "@fortawesome/pro-regular-svg-icons";

import { AnalyticsCopilotSidebar } from "src/analytics/copilot/AnalyticsCopilotSidebar";
import type { ChartExploreContext } from "src/analytics/copilot/chartExploreContext";
import type { DashboardPinning } from "src/analytics/copilot/LiveAnalyticsMessages";
import { MAX_PINS, useDashboard } from "src/analytics/dashboard/useDashboard";
import { isPerformanceDashboard } from "src/api/analytics-dashboard/fixedDashboards";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { Realm } from "src/clients/analytics-dashboard";
import { useLiveAnalyticsChat } from "src/flow/analytics/m1/useLiveAnalyticsChat";
import { useLiveAnalyticsAccess } from "src/flow/analytics/useLiveAnalyticsAccess";
import { useSpecialDomainLabels } from "src/flow/analytics/useSpecialDomainLabels";

const SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  {
    label: "How is my decision volume progressing over time?",
    prompt: "How is my decision volume progressing over time?",
  },
  {
    label: "Which KPIs should I track to evaluate decision flow performance?",
    prompt: "Which KPIs should I track to evaluate decision flow performance?",
  },
  {
    label: "Show me the decision latency split by P50, P90, Max",
    prompt: "Show me the decision latency split by P50, P90, Max",
  },
];

export const CopilotSidebar: React.FC<{
  onClose: () => void;
  flowId: string;
  flowName: string;
  dashboardId?: string;
  isLayoutEditing?: boolean;
  pendingStructuredInput?: ChartExploreContext | null;
  onClearPendingStructuredInput?: () => void;
}> = ({
  onClose,
  flowId,
  flowName,
  dashboardId,
  isLayoutEditing = false,
  pendingStructuredInput,
  onClearPendingStructuredInput,
}) => {
  const chat = useLiveAnalyticsChat({
    flowId,
    flowName,
  });
  const labelDictionaries = useSpecialDomainLabels(flowId, flowName);
  const {
    dashboard,
    pinChart,
    unpinChart,
    isAtPinCap,
    isLoading,
    hasLoadFailed,
  } = useDashboard(Realm.FLOWS, flowId, dashboardId);
  const { canAccess: canPin } = useLiveAnalyticsAccess();
  const isReadOnlyDashboard = isPerformanceDashboard(Realm.FLOWS, dashboardId);

  const pinning: DashboardPinning = {
    dashboard,
    pinChart,
    unpinChart,
    pinDisabled:
      isLoading ||
      hasLoadFailed ||
      !canPin ||
      isAtPinCap ||
      isLayoutEditing ||
      isReadOnlyDashboard,
    pinDisabledReason: isReadOnlyDashboard
      ? "The Performance dashboard is read-only, switch to Analytics to pin a chart"
      : hasLoadFailed
        ? "The dashboard failed to load, reload the page to pin charts"
        : isLayoutEditing
          ? "Pinning is disabled in edit mode, save your layout to add new charts"
          : isAtPinCap
            ? `Dashboards hold up to ${MAX_PINS} charts, remove one to pin a new chart`
            : undefined,
  };

  return (
    <AnalyticsCopilotSidebar
      chat={chat}
      contextPill={{
        icon: faDiagramNested,
        label: flowName,
        key: "flow-context",
      }}
      dataLoc="copilot-sidebar"
      emptyStateDataLoc="copilot-empty-state"
      labelDictionaries={labelDictionaries}
      onClearPendingStructuredInput={onClearPendingStructuredInput}
      onClose={onClose}
      pendingStructuredInput={pendingStructuredInput}
      pinning={pinning}
      storageKey="copilot-sidebar-width"
      submitLabel={chat.messages.length > 0 ? "Send" : "Start Chat"}
      suggestionPrompts={SUGGESTION_PROMPTS}
    />
  );
};
